import React, { useState } from 'react';
import { SparklesIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import releaseNotes from '../data/releaseNotes';

export default function ReleaseNotes() {
  const [expandedVersions, setExpandedVersions] = useState(
    // Auto-expand the latest 3 versions
    new Set(releaseNotes.slice(0, 3).map(r => r.version))
  );

  const toggleVersion = (version) => {
    setExpandedVersions(prev => {
      const next = new Set(prev);
      if (next.has(version)) next.delete(version);
      else next.add(version);
      return next;
    });
  };

  const expandAll = () => setExpandedVersions(new Set(releaseNotes.map(r => r.version)));
  const collapseAll = () => setExpandedVersions(new Set());

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <SparklesIcon className="h-6 w-6 text-blue-400" />
          <h1 className="text-xl font-bold text-gray-100">Release Notes</h1>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={expandAll} className="text-gray-500 hover:text-gray-300 transition-colors">
            Expand all
          </button>
          <span className="text-gray-700">|</span>
          <button onClick={collapseAll} className="text-gray-500 hover:text-gray-300 transition-colors">
            Collapse all
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {releaseNotes.map((release, idx) => {
          const isExpanded = expandedVersions.has(release.version);
          const isLatest = idx === 0;

          return (
            <div
              key={release.version}
              className={`card p-0 overflow-hidden ${isLatest ? 'border-blue-700/40' : ''}`}
            >
              <button
                onClick={() => toggleVersion(release.version)}
                className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-800/30 transition-colors"
              >
                <ChevronRightIcon
                  className={`h-4 w-4 text-gray-600 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                />
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`font-mono text-sm font-semibold ${isLatest ? 'text-blue-400' : 'text-gray-300'}`}>
                    v{release.version}
                  </span>
                  {release.title && (
                    <span className="text-sm text-gray-400 truncate">
                      {release.title}
                    </span>
                  )}
                  {isLatest && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-900/50 text-blue-300 border border-blue-700/40 flex-shrink-0">
                      Latest
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-600 flex-shrink-0 font-mono">
                  {release.date}
                </span>
              </button>

              {isExpanded && (
                <div className="px-5 pb-4 pt-1 border-t border-gray-800/50">
                  <ul className="space-y-2">
                    {release.changes.map((change, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-400">
                        <span className="text-gray-600 flex-shrink-0 mt-0.5">&bull;</span>
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
